'use client'

import { useEffect, useRef, useState } from 'react'

const steps = [
  {
    number: '01',
    title: 'Free Discovery Call',
    description:
      'In a 30-minute call, we map your current workflows and identify your biggest time-wasters. You will walk away with a clear picture of automation opportunities—even if you decide not to work with us. No cost, no obligation.',
  },
  {
    number: '02',
    title: 'Working Prototype in Days',
    description:
      'We build a functional prototype fast—typically within 1-2 weeks. You will see and test real software, not mockups or slide decks. This is where most clients have their "aha moment" and realize what is possible.',
  },
  {
    number: '03',
    title: 'Refine Until Perfect',
    description:
      'Your feedback shapes every iteration. We refine the solution until it fits your workflow exactly. Most projects reach full production-ready status within 4-8 weeks. You are involved at every step—no surprises.',
  },
  {
    number: '04',
    title: 'Launch and Scale',
    description:
      'Go live with confidence. We handle deployment, monitoring, and support. You get monthly usage reports and can request updates anytime. As your business grows, your software grows with you—no rebuild required.',
  },
]

export default function HowWeWork() {
  const sectionRef = useRef<HTMLElement>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const [visibleSteps, setVisibleSteps] = useState<Set<number>>(new Set())
  const [highlightedStep, setHighlightedStep] = useState<number | null>(null)

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = stepRefs.current.findIndex((ref) => ref === entry.target)
          if (index !== -1) {
            setVisibleSteps((prev) => {
              const next = new Set(prev)
              if (entry.isIntersecting) {
                next.add(index)
              }
              return next
            })
          }
        })
      },
      { threshold: 0.3, rootMargin: '0px 0px -100px 0px' }
    )

    const observedRefs = [...stepRefs.current]

    // Observe all step items
    observedRefs.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    // Track scroll position for highlighting
    const handleScroll = () => {
      if (!section) return

      const viewportCenter = window.scrollY + window.innerHeight / 2
      const sectionRect = section.getBoundingClientRect()
      const sectionTop = sectionRect.top + window.scrollY
      const sectionBottom = sectionTop + sectionRect.height

      // Check if viewport center is within the section bounds
      const isInSection = viewportCenter >= sectionTop && viewportCenter <= sectionBottom

      if (!isInSection) {
        // If outside the section, clear highlight
        setHighlightedStep(null)
        return
      }

      // Calculate which step should be highlighted based on scroll position
      let currentHighlight: number | null = null
      let closestDistance = Infinity

      stepRefs.current.forEach((ref, index) => {
        if (!ref) return
        const rect = ref.getBoundingClientRect()
        const elementTop = rect.top + window.scrollY
        const elementCenter = elementTop + rect.height / 2

        // Calculate distance from viewport center to element center
        const distance = Math.abs(viewportCenter - elementCenter)

        // Always find the closest step when in section
        if (distance < closestDistance) {
          closestDistance = distance
          currentHighlight = index
        }
      })

      // Always set a highlight when in section (should always find one)
      setHighlightedStep(currentHighlight)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => {
      observedRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref)
      })
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <section ref={sectionRef} id="how-we-work" className="how-we-work-section">
      <div className="container">
        <div className="how-we-work-header">
          <div className="how-we-work-title-row">
            <h2 className="how-we-work-title font-logo">How We Work</h2>
            <div className="how-we-work-separator"></div>
          </div>
          <div className="how-we-work-description-row">
            <div className="how-we-work-title-spacer"></div>
            <p className="how-we-work-description">
              From first call to live software in weeks, not months. We follow a proven 4-step process that has delivered 100+ successful projects. Every step is designed to minimize your risk and maximize your results—you see progress immediately and pay nothing until you are getting value.
            </p>
          </div>
        </div>
        <div className="how-we-work-steps">
          {steps.map((step, index) => {
            const isVisible = visibleSteps.has(index)
            const isHighlighted = highlightedStep === index

            return (
              <div
                key={index}
                ref={(el) => {
                  stepRefs.current[index] = el
                }}
                className="how-we-work-step"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                }}
              >
                <div className="how-we-work-step-content">
                  <div className="how-we-work-step-left">
                    <div
                      className="how-we-work-step-number"
                      style={{
                        color: isHighlighted ? 'var(--color-primary)' : '#5d5d5d',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {step.number}
                    </div>
                    <h3
                      className="how-we-work-step-title"
                      style={{
                        color: isHighlighted ? '#ffffff' : '#5d5d5d',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {step.title.toUpperCase()}
                    </h3>
                  </div>
                  <p
                    className="how-we-work-step-description"
                    style={{
                      color: isHighlighted ? 'var(--color-primary)' : '#5d5d5d',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
